import { useState } from 'react'
import { Icon } from '../components/Icon.js'
import { PageHeader } from '../components/PageHeader.js'
import { logout } from '../lib/api.js'
import { useStatus } from '../lib/items.js'

function Row({ title, on, children }: { title: string; on: boolean | undefined; children: React.ReactNode }): JSX.Element {
  return (
    <div className="px-4 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-callout font-semibold">{title}</p>
        <span className={`rounded-full px-2.5 py-0.5 text-caption font-semibold ${on ? 'bg-success/15 text-success' : 'bg-fill/[0.12] text-label2/60'}`}>
          {on === undefined ? '…' : on ? 'aktiv' : 'nicht eingerichtet'}
        </span>
      </div>
      <div className="mt-1 text-small text-label2/65">{children}</div>
    </div>
  )
}

/** Connections and how to set them up. The connector URL lives here, ready to copy. */
export function Settings({ onLogout }: { onLogout: () => void }): JSX.Element {
  const { data } = useStatus()
  const [copied, setCopied] = useState(false)

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-5 py-6 md:px-8">
      <PageHeader title="Verbindungen" />

      <section>
        <p className="section-title px-0">Claude</p>
        <div className="inset-group">
          <Row title="Konnektor" on={data?.connector}>
            Claude liest und bearbeitet den Plan direkt – in claude.ai, auf dem iPhone und in Claude Code.
            {data?.connectorUrl && (
              <div className="mt-3 space-y-3">
                <div className="flex items-center gap-2 rounded-[12px] bg-fill/[0.08] p-2 pl-3">
                  <code className="min-w-0 flex-1 truncate text-caption text-label">{data.connectorUrl}</code>
                  <button
                    type="button"
                    className="btn-ghost min-h-[32px] px-3 text-small"
                    onClick={() =>
                      void navigator.clipboard.writeText(data.connectorUrl!).then(() => {
                        setCopied(true)
                        setTimeout(() => setCopied(false), 1800)
                      })
                    }
                  >
                    {copied ? 'Kopiert' : 'Kopieren'}
                  </button>
                </div>
                <ol className="list-decimal space-y-1 pl-5">
                  <li>claude.ai → Einstellungen → Konnektoren → „Benutzerdefinierten Konnektor hinzufügen“</li>
                  <li>Name „Marketing Hub“, diese Adresse einfügen, keine Client-ID nötig</li>
                  <li>„Verbinden“ → Code an marketing@pebro.de anfordern → eingeben</li>
                </ol>
                <p>
                  Claude Code: <code className="text-caption">claude mcp add --transport http marketing-hub {data.connectorUrl}</code>, danach
                  <code className="text-caption"> /mcp</code> → Anmelden.
                </p>
              </div>
            )}
          </Row>
        </div>
      </section>

      <section>
        <p className="section-title px-0">Daten</p>
        <div className="inset-group">
          <Row title="Datei-Vorschauen (Microsoft 365)" on={data?.files}>
            Zeigt Bilder und Videos aus dem Kampagnenordner im Eintrag. Braucht eine Freigabe im Microsoft-365-Admin, nur lesend.
          </Row>
          <Row title="Anlässe" on>
            Feiertage, Zeitumstellung, Black Friday, Advent … mit Vorlauf, laufend für die nächsten 60 Tage.
          </Row>
        </div>
      </section>

      <section>
        <p className="section-title px-0">Tastatur (Mac)</p>
        <div className="inset-group">
          {[
            ['N', 'Neuer Eintrag'],
            ['/', 'Suchen'],
            ['1 – 5', 'Bereich wechseln'],
            ['Esc', 'Fenster schließen'],
          ].map(([k, label]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5 text-small">
              <span>{label}</span>
              <kbd className="rounded-[6px] bg-fill/[0.12] px-2 py-0.5 font-sans text-caption font-semibold">{k}</kbd>
            </div>
          ))}
        </div>
      </section>

      <button type="button" onClick={() => void logout().then(onLogout)} className="flex items-center gap-2 text-small text-danger">
        <Icon name="logout" size={16} /> Abmelden
      </button>
    </div>
  )
}
